import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

export default function AdminLayout() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);
  const userRole = localStorage.getItem("username") || "admin";

  // Unified Menu Items Configuration Matrix
  const navLinks = [
    { to: "/admin/dashboard", label: "Dashboard" },
    { to: "/admin/comittee", label: "Committee" },
    { to: "/admin/event", label: "Event" },
    { to: "/admin/vlog", label: "Vlog" },
    ...(userRole === "superadmin"
      ? [{ to: "/admin/donation", label: "Approve" }]
      : []),
    { to: "/admin/donationRecord", label: "Donation" },
    { to: "/admin/member", label: "Members" },
    { to: "/admin/notice", label: "Notice" },
  ];

  // Modern Navigation Link Styling Engine
  const linkStyles = ({ isActive }) =>
    `flex items-center px-4 py-3 rounded-xl font-medium tracking-wide transition-all duration-200 ${
      isActive
        ? "bg-amber-500 text-slate-950 shadow-lg shadow-amber-500/20 scale-[1.02]"
        : "text-emerald-100/70 hover:bg-emerald-900/40 hover:text-white"
    }`;

  // Shared Sidebar Component (avoids repeating markup between Desktop & Mobile)
  const SidebarContent = ({ onLinkClick = () => {} }) => (
    <div className="flex flex-col h-full justify-between">
      <div className="space-y-8">
        {/* Profile Card Block */}
        <div className="text-center bg-emerald-950/40 border border-emerald-800/20 p-5 rounded-2xl">
          <div className="relative w-20 h-20 mx-auto rounded-full bg-gradient-to-tr from-amber-400 to-emerald-600 p-[2px] shadow-xl">
            <div className="w-full h-full bg-slate-900 rounded-full flex items-center justify-center text-white font-serif text-2xl font-bold tracking-wider">
              {userRole.charAt(0).toUpperCase()}
            </div>
          </div>
          <p className="mt-3 text-sm font-bold tracking-widest text-amber-400 uppercase">
            {userRole}
          </p>
          <span className="text-[11px] text-emerald-100/50 block mt-0.5">
            Authorized Portal
          </span>
        </div>

        {/* Dynamic Navigation Container */}
        <nav className="flex flex-col space-y-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={linkStyles}
              onClick={onLinkClick}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Decorative Branding Grounder */}
      <div className="text-center pt-6 border-t border-emerald-900/30 text-[10px] uppercase tracking-widest text-emerald-100/30">
        M.B.J.M Panel v2.0
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-stone-50">
      {/* 1. Permanent Sidebar Component for Desktop Screens */}
      <aside className="hidden md:flex flex-col bg-slate-900 border-r border-slate-800 text-white w-64 p-6 shrink-0 shadow-2xl">
        <SidebarContent />
      </aside>

      {/* 2. Responsive Application Main Entry viewport */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Sticky Mobile Application Banner Wrapper */}
        <header className="md:hidden flex items-center justify-between px-6 py-4 bg-slate-900 text-white shadow-md z-30">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm font-bold tracking-wider text-emerald-100">
              MBJM Admin
            </span>
          </div>
          <button
            onClick={openDrawer}
            className="p-1 text-emerald-100 hover:text-white transition-colors outline-none"
            aria-label="Open Navigation Menu"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              className="w-7 h-7"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </button>
        </header>

        {/* Dynamic Route View Renderer Outlet container */}
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      {/* 3. Mobile View Sliding Navigation Drawer Wrapper */}
      <div
        className={`fixed inset-0 z-50 transition-all duration-300 ${
          isDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Soft Backdrop Dimming Sheet Mask */}
        <div
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm transition-opacity duration-300"
          onClick={closeDrawer}
        />

        {/* Sliding Panel Content Frame */}
        <div
          className={`absolute top-0 left-0 bg-slate-900 text-white w-64 h-full p-6 shadow-2xl transform transition-transform duration-300 flex flex-col justify-between ${
            isDrawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Close Panel Button Group header */}
          <div className="flex justify-end mb-4">
            <button
              onClick={closeDrawer}
              className="p-1 text-emerald-100/70 hover:text-white transition-colors"
              aria-label="Close Drawer Menu"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-7 h-7"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18 18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <SidebarContent onLinkClick={closeDrawer} />
          </div>
        </div>
      </div>
    </div>
  );
}
